<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="close">
    <div class="modal-container">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">Seleziona Analisi da Confrontare</h2>
        <button class="close-button" @click="close">×</button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <img src="@/assets/Icons/Loading.gif" alt="Loading" class="loading-gif" />
        <p class="loading-text">Caricamento analisi...</p>
      </div>

      <!-- Content -->
      <div v-else class="modal-content">
        <!-- Empty State -->
        <div v-if="completedAnalyses.length === 0" class="empty-state">
          <p class="empty-text">Nessuna analisi completata disponibile per il confronto.</p>
        </div>

        <!-- Analyses List -->
        <div v-else class="analyses-list">
          <div
            v-for="analysis in completedAnalyses"
            :key="analysis.analysis_id"
            class="analysis-checkbox-item"
            @click="toggleAnalysis(analysis.analysis_id)"
          >
            <input
              type="checkbox"
              :id="`analysis-${analysis.analysis_id}`"
              :checked="selectedAnalyses.includes(analysis.analysis_id)"
              @click.stop="toggleAnalysis(analysis.analysis_id)"
            />
            <label :for="`analysis-${analysis.analysis_id}`" class="analysis-label">
              <span class="company-name">{{ analysis.company_name }}</span>
              <span class="analysis-date">{{ formatDate(analysis.created_at) }}</span>
            </label>
          </div>
        </div>

        <!-- Selection Info -->
        <div v-if="completedAnalyses.length > 0" class="selection-info">
          <p class="selection-text">
            {{ selectedAnalyses.length }} analisi selezionate
            <span v-if="selectedAnalyses.length < 2" class="selection-hint">
              (Seleziona almeno 2 analisi)
            </span>
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="cancel-button" @click="close">
          Annulla
        </button>
        <button
          class="show-button"
          :disabled="selectedAnalyses.length < 2"
          @click="showComparison"
        >
          Mostra Confronto
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useAnalysisStore } from '@/stores/analysisStore';
import { useAuthStore } from '@/stores/authStore';

export default {
  name: 'MultiAnalysisModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const analysisStore = useAnalysisStore();
    return { analysisStore };
  },
  data() {
    return {
      loading: false,
      selectedAnalyses: [],
      completedAnalyses: [],
      userId: null,
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.loadCompletedAnalyses();
      }
    },
  },
  async mounted() {
    const authStore = useAuthStore();
    const user = authStore.user;
    if (user) {
      this.userId = user.id;
    }
  },
  methods: {
    async loadCompletedAnalyses() {
      if (!this.userId) return;

      try {
        this.loading = true;
        
        // Fetch all completed analyses (with a high limit)
        const data = await this.analysisStore.fetchUserAnalyses(this.userId, {
          status: 'completed',
          limit: 100,
          offset: 0,
        });

        this.completedAnalyses = data.analyses || [];
        this.loading = false;
      } catch (error) {
        console.error('Error loading completed analyses:', error);
        this.loading = false;
      }
    },

    toggleAnalysis(analysisId) {
      const index = this.selectedAnalyses.indexOf(analysisId);
      if (index === -1) {
        this.selectedAnalyses.push(analysisId);
      } else {
        this.selectedAnalyses.splice(index, 1);
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const options = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      };
      return date.toLocaleDateString('it-IT', options);
    },

    close() {
      this.selectedAnalyses = [];
      this.$emit('close');
    },

    showComparison() {
      if (this.selectedAnalyses.length < 2) return;
      this.$emit('compare', this.selectedAnalyses);
      this.selectedAnalyses = [];
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: white;
  border: 2px solid #2e4eed;
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.close-button:hover {
  color: #1a1a1a;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1rem;
}

.loading-gif {
  width: 50px;
  height: 50px;
}

.loading-text {
  font-size: 1rem;
  color: #6b7280;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.empty-text {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0;
}

.analyses-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.analysis-checkbox-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.analysis-checkbox-item:hover {
  border-color: #2e4eed;
  background: rgba(46, 78, 237, 0.02);
}

.analysis-checkbox-item input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.analysis-label {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 1rem;
}

.company-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
}

.analysis-date {
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
}

.selection-info {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #e5e7eb;
}

.selection-text {
  font-size: 1rem;
  color: #1a1a1a;
  margin: 0;
  text-align: center;
}

.selection-hint {
  color: #ef4444;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 2px solid #e5e7eb;
}

.cancel-button,
.show-button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid;
}

.cancel-button {
  background: white;
  border-color: #6b7280;
  color: #6b7280;
}

.cancel-button:hover {
  background: #f3f4f6;
  border-color: #1a1a1a;
  color: #1a1a1a;
}

.show-button {
  background: #2e4eed;
  border-color: #2e4eed;
  color: white;
}

.show-button:hover:not(:disabled) {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.show-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-container {
    max-height: 90vh;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-content {
    padding: 1rem;
  }

  .analysis-label {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .company-name {
    font-size: 1rem;
  }

  .modal-footer {
    padding: 1rem;
  }
}
</style>
